import { Link } from "react-router-dom";
import LegalLayout from "./LegalLayout";

export default function DPA() {
  return (
    <LegalLayout title="DATA PROCESSING AGREEMENT (DPA)" subtitle="GDPR Art. 28 · LGPD · LFPDPPP · CCPA">
      <p className="text-[#5C5C5C] mb-6">
        Versión 1.0 · Última actualización: 29 de Abril de 2026
      </p>

      <p>
        Este Acuerdo de Procesamiento de Datos (en adelante "DPA") se incorpora por
        referencia al <Link to="/legal/contract" className="underline">Contrato de Prestación de
        Servicios</Link> suscrito entre <b>CIA Labs S.A.P.I. de C.V.</b> (el "Procesador de Datos" o "CIA") y el cliente
        (el "Responsable de Datos" o "Cliente") que utiliza la Plataforma <b>CIA Marketing®</b>.
      </p>

      <h2>1. OBJETO Y MARCO LEGAL</h2>
      <p>
        Este DPA rige el tratamiento de datos personales realizado por CIA como encargado del
        tratamiento por cuenta del Cliente, en cumplimiento de:
      </p>
      <ul>
        <li>Reglamento (UE) 2016/679 — <b>GDPR</b> (Unión Europea)</li>
        <li>Lei 13.709/2018 — <b>LGPD</b> (Brasil)</li>
        <li>Ley Federal de Protección de Datos Personales en Posesión de los Particulares — <b>LFPDPPP</b> (México)</li>
        <li>California Consumer Privacy Act — <b>CCPA</b> (California, USA)</li>
        <li>UK GDPR (Reino Unido) · PIPEDA (Canadá) · POPIA (Sudáfrica)</li>
      </ul>

      <h2>2. DEFINICIONES</h2>
      <ul>
        <li><b>Datos Personales:</b> cualquier información relativa a una persona física identificada o identificable.</li>
        <li><b>Titular de Datos:</b> la persona física cuyos Datos Personales son tratados.</li>
        <li><b>Responsable de Datos (Controller):</b> el Cliente, quien determina los fines y medios del tratamiento.</li>
        <li><b>Procesador de Datos (Processor):</b> CIA Labs S.A.P.I. de C.V., quien trata los Datos Personales por cuenta del Responsable.</li>
        <li><b>Subprocesadores:</b> terceros contratados por CIA para prestar partes del Servicio (proveedores cloud, modelos IA, etc.).</li>
      </ul>

      <h2>3. ROLES Y OBLIGACIONES</h2>
      <p>
        El <b>Cliente es el Responsable</b> de los Datos Personales que sube a la Plataforma y garantiza tener base legal para su tratamiento.
        <b> CIA es el Procesador</b> y solo tratará Datos Personales siguiendo instrucciones documentadas del Cliente, a menos que la ley aplicable le obligue a otra cosa.
      </p>

      <h2>4. NATURALEZA Y FINALIDAD DEL TRATAMIENTO</h2>
      <ul>
        <li><b>Finalidad:</b> prestar el Servicio de automatización de marketing (generación de contenido con IA, programación en redes sociales, análisis, facturación, soporte).</li>
        <li><b>Duración:</b> mientras exista una relación contractual activa + 30 días de export post-terminación.</li>
        <li><b>Categorías de Datos:</b> nombre, email, IP, datos de facturación, contenido generado, métricas de redes sociales, tokens OAuth de plataformas conectadas.</li>
        <li><b>Categorías de Titulares:</b> clientes del Cliente, empleados del Cliente, suscriptores, leads generados.</li>
        <li><b>Datos sensibles:</b> CIA <b>no procesa</b> categorías especiales (salud, religión, orientación política/sexual, biométricos) salvo que el Cliente las suba voluntariamente, bajo su exclusiva responsabilidad.</li>
      </ul>

      <h2>5. OBLIGACIONES DE CIA COMO PROCESADOR</h2>
      <p>CIA se compromete a:</p>
      <ul>
        <li>Tratar los Datos Personales <b>solo bajo instrucciones documentadas</b> del Cliente (uso de la Plataforma = instrucción).</li>
        <li>Garantizar que el personal autorizado mantenga <b>confidencialidad</b> (NDAs firmados).</li>
        <li>Implementar medidas técnicas y organizativas apropiadas: TLS 1.3, AES-256 at rest, 2FA, backups diarios, monitoreo 24/7, principio de mínimo privilegio, logs auditables.</li>
        <li><b>No utilizar los Datos Personales del Cliente para entrenar modelos de IA propios ni de terceros.</b></li>
        <li><b>No vender ni compartir</b> los Datos Personales del Cliente con terceros para fines comerciales.</li>
        <li>Asistir al Cliente en el cumplimiento de sus obligaciones (responder solicitudes de titulares, evaluaciones de impacto DPIA).</li>
        <li>Notificar cualquier brecha de seguridad al Cliente en un plazo máximo de <b>72 horas</b> desde su detección.</li>
      </ul>

      <h2>6. SUBPROCESADORES AUTORIZADOS</h2>
      <p>El Cliente autoriza a CIA a utilizar los siguientes subprocesadores para prestar el Servicio:</p>
      <table>
        <thead>
          <tr><th>Subprocesador</th><th>Finalidad</th><th>Localización</th></tr>
        </thead>
        <tbody>
          <tr><td>DigitalOcean</td><td>Hosting / compute / base de datos</td><td>USA</td></tr>
          <tr><td>Stripe</td><td>Procesamiento de pagos</td><td>USA · Irlanda</td></tr>
          <tr><td>SendGrid (Twilio)</td><td>Emails transaccionales</td><td>USA</td></tr>
          <tr><td>OpenAI</td><td>Modelos de IA (GPT-5.2, Whisper, DALL-E)</td><td>USA</td></tr>
          <tr><td>Anthropic</td><td>Modelos de IA (Claude Sonnet/Opus)</td><td>USA</td></tr>
          <tr><td>Google Cloud</td><td>Modelos de IA (Gemini, Nano Banana, Sora)</td><td>USA · EU</td></tr>
          <tr><td>Facturama</td><td>Emisión de CFDI (México)</td><td>México</td></tr>
          <tr><td>Cloudflare</td><td>CDN / DNS / WAF</td><td>Global</td></tr>
        </tbody>
      </table>
      <p>
        CIA publicará cualquier cambio de subprocesadores en esta página con al menos <b>30 días de antelación</b>.
        El Cliente podrá objetar por motivos razonables de protección de datos; si no se alcanza un acuerdo,
        el Cliente podrá terminar el contrato.
      </p>

      <h2>7. TRANSFERENCIAS INTERNACIONALES</h2>
      <p>
        Para titulares ubicados en la UE/EEE cuyos datos se transfieran a países fuera del espacio con nivel adecuado,
        CIA se basa en las <b>Cláusulas Contractuales Tipo (SCCs)</b> de la Comisión Europea (Decisión 2021/914),
        así como en el <b>EU-US Data Privacy Framework</b> donde aplique (principales subprocesadores están certificados).
      </p>

      <h2>8. DERECHOS DE LOS TITULARES</h2>
      <p>
        CIA asistirá al Cliente para responder solicitudes de titulares (ARCO en México, derechos GDPR Art. 15-22 en EU,
        derechos CCPA en California, etc.): acceso, rectificación, supresión, portabilidad, oposición, limitación.
      </p>
      <p>
        Si un titular contacta directamente a CIA, CIA redirigirá la solicitud al Cliente y notificará al titular.
        Los titulares pueden ejercer sus derechos escribiendo a <b>privacy@ciamkt.com</b>.
      </p>

      <h2>9. BRECHAS DE SEGURIDAD</h2>
      <p>
        En caso de brecha que afecte a Datos Personales del Cliente, CIA notificará por email al contacto registrado
        en la cuenta en un plazo máximo de <b>72 horas</b> desde la detección, con:
      </p>
      <ul>
        <li>Naturaleza de la brecha, categorías y número aproximado de titulares afectados.</li>
        <li>Nombre y contacto del DPO o punto de contacto.</li>
        <li>Consecuencias probables y medidas adoptadas o propuestas.</li>
      </ul>

      <h2>10. AUDITORÍAS</h2>
      <p>
        CIA pondrá a disposición del Cliente toda la información necesaria para demostrar el cumplimiento de este DPA,
        incluyendo reportes de seguridad, logs y certificaciones. Auditorías in-situ pueden acordarse bajo NDA,
        con al menos 30 días de aviso, no más de una vez al año (excepto tras una brecha confirmada), y con costos
        razonables a cargo del Cliente salvo que la auditoría revele un incumplimiento material.
      </p>

      <h2>11. TERMINACIÓN Y DEVOLUCIÓN DE DATOS</h2>
      <p>
        Tras la terminación del contrato, el Cliente tendrá <b>30 días</b> para exportar todos sus datos desde el dashboard.
        Transcurrido ese plazo, CIA eliminará de forma segura los Datos Personales del Cliente de sus sistemas de producción
        en un máximo de <b>90 días</b>, excepto los que deba conservar por obligación legal (facturación fiscal por 5 años en México,
        logs de seguridad por 6 meses).
      </p>

      <h2>12. LIMITACIÓN DE RESPONSABILIDAD</h2>
      <p>
        La responsabilidad de CIA bajo este DPA se rige por la cláusula de Limitación de Responsabilidad del
        <Link to="/legal/contract" className="underline"> Contrato de Prestación de Servicios</Link> principal
        (limitada al importe pagado en el ciclo de facturación mensual en curso).
      </p>

      <h2>13. CONTACTO DEL DPO</h2>
      <p>
        Para cualquier asunto relacionado con privacidad o protección de datos, contacta al <b>Data Protection Officer</b>:
      </p>
      <ul>
        <li>Email: <b>privacy@ciamkt.com</b></li>
        <li>Dirección: CIA Labs S.A.P.I. de C.V. · Guadalajara, Jalisco, México</li>
      </ul>

      <h2>14. MODIFICACIONES</h2>
      <p>
        CIA podrá actualizar este DPA para reflejar cambios regulatorios. Los cambios materiales se notificarán
        por email con al menos <b>30 días</b> de antelación. El uso continuado del Servicio tras la notificación
        constituye aceptación del DPA actualizado.
      </p>

      <h2>15. JURISDICCIÓN</h2>
      <p>
        Este DPA se rige por la misma jurisdicción elegida en el Contrato principal:
      </p>
      <ul>
        <li><b>Versión Español · México:</b> Leyes mexicanas · Tribunales de CDMX.</li>
        <li><b>Versión English · Internacional:</b> Leyes de Delaware (USA) · Arbitraje AAA.</li>
      </ul>

      <hr className="my-8 border-[#0A0A0A]/20" />
      <p className="text-xs text-[#5C5C5C]">
        <b>Incorporación por referencia:</b> Este DPA forma parte integral del Contrato de Prestación de Servicios.
        Al aceptar el Contrato o usar la Plataforma, el Cliente acepta también este DPA. En caso de conflicto
        entre el Contrato y este DPA respecto al tratamiento de Datos Personales, prevalecerá este DPA.
      </p>
    </LegalLayout>
  );
}
